import React, { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import InfoText from 'components/shared/info-text.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { requestSupplierConnectionTemplate } from 'apollo/templates/supplier-request';
import { SupplierConnectionState, SupplierDirectoryLab } from 'models/supplier-request';

type RequestSupplierDialogProps = {
    lab?: SupplierDirectoryLab;
    onClose: () => void;
};

const MAX_MESSAGE_LENGTH = 1000;

/** DD-3662: ask a listed lab to become this lab's supplier, with an optional note. */
const RequestSupplierDialog: FC<RequestSupplierDialogProps> = ({ lab, onClose }) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [message, setMessage] = useState('');

    const close = () => {
        setMessage('');
        onClose();
    };

    const [sendRequest, { loading: isSending }] = useMutation(requestSupplierConnectionTemplate, {
        refetchQueries: ['outgoingSupplierRequests'],
        // The card turns into "Request sent" without reloading the browser's pages.
        update: (cache) => {
            if (!lab) return;
            cache.modify({
                id: cache.identify({ __typename: 'SupplierDirectoryLab', _id: lab._id }),
                fields: { connection: () => SupplierConnectionState.PENDING },
            });
        },
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('client-directory:SUPPLIER_REQUEST_SENT', { lab: lab?.name }),
            });
            close();
        },
        onError: (error) => showBoundary(error),
    });

    return (
        <DHDialog
            open={Boolean(lab)}
            onClose={close}
            fullWidth
            maxWidth="sm"
            title={translate('client-directory:REQUEST_SUPPLIER_TITLE', { lab: lab?.name })}
            actions={
                <Button
                    variant="contained"
                    disabled={isSending}
                    onClick={() =>
                        lab && sendRequest({ variables: { supplierId: lab._id, message: message.trim() || undefined } })
                    }
                    data-test="supplier-browser.send-request"
                >
                    {translate('client-directory:SEND_SUPPLIER_REQUEST')}
                </Button>
            }
        >
            <InfoText text={translate('client-directory:REQUEST_SUPPLIER_INFO', { lab: lab?.name })} />
            <TextField
                sx={{ mt: 2 }}
                fullWidth
                multiline
                minRows={4}
                label={translate('client-directory:REQUEST_SUPPLIER_MESSAGE')}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'data-test': 'supplier-browser.request-message' }}
            />
        </DHDialog>
    );
};

export default RequestSupplierDialog;
